import { useEffect, useMemo, useState } from "react";
import { Gavel, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { rm, fmtDate, toLocalInput } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { useInfiniteCatalogProducts } from "../hooks/useInfiniteCatalogProducts";
import { Button, Input, SearchSelect, Badge, statusColor, useConfirm, parseDecimalField, parseIntField } from "../components/ui";
import { DataTable, type Column, type DataTableRowActionItem, type DataTableRowActions } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { useQueryRecordId } from "../lib/use-query-record";

interface ProductRef {
  id: string;
  name: string;
  images?: string[];
}

interface AuctionRow {
  id: string;
  title: string;
  status: string;
  startPrice: number;
  reservePrice: number | null;
  buyNowPrice: number | null;
  currentPrice: number;
  bidCount: number;
  startsAt: string;
  endsAt: string;
  antiSnipeMinutes: number;
  product: ProductRef;
  description?: string | null;
}

interface AuctionForm {
  title: string;
  productId: string;
  startPrice: string;
  reservePrice: string;
  buyNowPrice: string;
  startsAt: string;
  endsAt: string;
  antiSnipeMinutes: string;
  paymentDeadlineHours: string;
}

const emptyForm = (): AuctionForm => ({
  title: "",
  productId: "",
  startPrice: "1",
  reservePrice: "",
  buyNowPrice: "",
  startsAt: toLocalInput(new Date()),
  endsAt: toLocalInput(new Date(Date.now() + 3 * 86400000)),
  antiSnipeMinutes: "5",
  paymentDeadlineHours: "48",
});

export function Auctions() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AuctionRow | null>(null);
  const [form, setForm] = useState<AuctionForm>(emptyForm);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const { recordId, clearRecordId } = useQueryRecordId();

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 250);
    return () => globalThis.clearTimeout(t);
  }, [productSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ["auctions"],
    queryFn: () => api<{ items: AuctionRow[] }>("/auctions?all=true"),
  });

  const productsQuery = useInfiniteCatalogProducts({
    enabled: editorOpen,
    search: debouncedProductSearch,
    queryKey: ["auction-mechanics"],
    params: { forMechanics: "true" },
  });

  const productOptions = useMemo(
    () =>
      productsQuery.items.map((p) => ({
        value: p.id,
        label: p.name,
        image: p.images?.[0] ?? null,
      })),
    [productsQuery.items],
  );

  const saveMut = useMutation({
    mutationFn: (payload: AuctionForm & { id?: string }) => {
      const body = {
        title: payload.title,
        productId: payload.productId,
        startPrice: parseDecimalField(payload.startPrice, 1),
        reservePrice: payload.reservePrice.trim() ? parseDecimalField(payload.reservePrice) : null,
        buyNowPrice: payload.buyNowPrice.trim() ? parseDecimalField(payload.buyNowPrice) : null,
        startsAt: new Date(payload.startsAt).toISOString(),
        endsAt: new Date(payload.endsAt).toISOString(),
        antiSnipeMinutes: parseIntField(payload.antiSnipeMinutes, 5),
        paymentDeadlineHours: parseIntField(payload.paymentDeadlineHours, 48),
      };
      if (payload.id) return api(`/auctions/${payload.id}`, { method: "PUT", body });
      return api<{ id: string }>("/auctions", { method: "POST", body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auctions"] });
      toast("success", editing ? "Auction updated" : "Auction created");
      closeEditor();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const endMut = useMutation({
    mutationFn: (id: string) => api(`/auctions/${id}/end`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auctions"] });
      toast("success", "Auction ended");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "End failed"),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => api(`/auctions/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auctions"] });
      toast("success", "Auction cancelled");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Cancel failed"),
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: AuctionRow) {
    if (row.bidCount > 0) {
      toast("error", "Auctions with bids cannot be edited");
      return;
    }
    setEditing(row);
    setForm({
      title: row.title,
      productId: row.product.id,
      startPrice: String(row.startPrice),
      reservePrice: row.reservePrice != null ? String(row.reservePrice) : "",
      buyNowPrice: row.buyNowPrice != null ? String(row.buyNowPrice) : "",
      startsAt: toLocalInput(row.startsAt),
      endsAt: toLocalInput(row.endsAt),
      antiSnipeMinutes: String(row.antiSnipeMinutes),
      paymentDeadlineHours: "48",
    });
    setEditorOpen(true);
  }

  useEffect(() => {
    if (!recordId || !data?.items) return;
    const row = data.items.find((item) => item.id === recordId);
    if (!row) return;
    openEdit(row);
    clearRecordId();
  }, [recordId, data?.items, clearRecordId]);

  useBottomBarActions(() => {
    if (!can("auctions.write")) return [];
    if (editorOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: editing ? "Save" : "Create",
          variant: "primary" as const,
          disabled: saveMut.isPending || !form.title.trim() || !form.productId,
          onClick: () => saveMut.mutate({ ...form, id: editing?.id }),
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
  }

  function setField<K extends keyof AuctionForm>(key: K, value: AuctionForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const columns: Column<AuctionRow>[] = [
    {
      key: "title",
      header: "Title",
      sortable: true,
      sortValue: (r) => r.title,
      render: (r) => <span className="font-medium">{r.title}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "product",
      header: "Product",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.product.name,
      render: (r) => r.product.name,
    },
    {
      key: "current",
      header: "Current",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.currentPrice || r.startPrice,
      exportValue: (r) => r.currentPrice || r.startPrice,
      render: (r) => rm(r.currentPrice || r.startPrice),
    },
    {
      key: "bids",
      header: "Bids",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.bidCount,
      render: (r) => <span className="tabular-nums">{r.bidCount}</span>,
    },
    {
      key: "ends",
      header: "Ends",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.endsAt,
      exportValue: (r) => fmtDate(r.endsAt),
      render: (r) => <span className="text-xs text-white/60">{fmtDate(r.endsAt)}</span>,
    },
  ];

  const auctionRowActions = useMemo((): DataTableRowActions<AuctionRow> => ({
    extra: (r) => {
      const items: DataTableRowActionItem[] = [];
      if (can("auctions.manage") && r.status === "LIVE") {
        items.push({
          label: "End",
          onClick: () =>
            confirm({
              title: "End auction now?",
              message: "The highest bidder will win if reserve is met.",
              confirmLabel: "End",
              action: () => endMut.mutateAsync(r.id),
            }),
        });
      }
      if (can("auctions.manage") && !["ENDED", "COMPLETED", "CANCELLED"].includes(r.status)) {
        items.push({
          label: "Cancel",
          variant: "danger",
          onClick: () =>
            confirm({
              title: "Cancel auction?",
              danger: true,
              confirmLabel: "Cancel",
              action: () => cancelMut.mutateAsync(r.id),
            }),
        });
      }
      return items;
    },
  }), [can, confirm, endMut, cancelMut]);

  return (
    <ListPageShell>
      {dialog}

      <DataTable
        columns={columns}
        rows={data?.items}
        rowKey={(r) => r.id}
        loading={isLoading}
        exportFilename="auctions"
        onRowClick={can("auctions.write") ? openEdit : undefined}
        activeKey={editing?.id}
        rowActions={auctionRowActions}
        empty={{
          icon: Gavel,
          title: "No auctions yet",
          hint: can("auctions.write") ? "Create an auction to get started." : undefined,
        }}
      />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit auction" : "New auction"}
        subtitle={editing?.product.name}
        onClose={closeEditor}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saveMut.isPending || !form.title || !form.productId}
              onClick={() => saveMut.mutate({ ...form, id: editing?.id })}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      >
        <div className="sidebar-form">
          <Input label="Title" value={form.title} onChange={(e) => setField("title", e.target.value)} />
          <SearchSelect
            label="Product"
            placeholder="Select product…"
            searchPlaceholder="Search products…"
            options={productOptions}
            value={form.productId}
            onChange={(v) => setField("productId", v)}
            onSearchChange={setProductSearch}
            serverFiltered
            hasMore={productsQuery.hasNextPage}
            loadingMore={productsQuery.isFetchingNextPage}
            onLoadMore={productsQuery.fetchNextPage}
            disabled={!!editing}
          />
          <Input
            label="Start price (RM)"
            type="number"
            min={0.01}
            step="0.01"
            value={form.startPrice}
            onChange={(e) => setField("startPrice", e.target.value)}
          />
          <Input
            label="Reserve price (RM)"
            type="number"
            min={0}
            step="0.01"
            hint="Optional — leave empty for no reserve"
            value={form.reservePrice}
            onChange={(e) => setField("reservePrice", e.target.value)}
          />
          <Input
            label="Buy now price (RM)"
            type="number"
            min={0}
            step="0.01"
            hint="Optional"
            value={form.buyNowPrice}
            onChange={(e) => setField("buyNowPrice", e.target.value)}
          />
          <div className="form-row-2">
            <Input
              label="Starts at"
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setField("startsAt", e.target.value)}
            />
            <Input
              label="Ends at"
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => setField("endsAt", e.target.value)}
            />
          </div>
          <Input
            label="Anti-snipe (minutes)"
            type="number"
            min={0}
            max={60}
            hint="Extends end time when bids arrive near close"
            value={form.antiSnipeMinutes}
            onChange={(e) => setField("antiSnipeMinutes", e.target.value)}
          />
          <Input
            label="Payment deadline (hours)"
            type="number"
            min={1}
            value={form.paymentDeadlineHours}
            onChange={(e) => setField("paymentDeadlineHours", e.target.value)}
          />
        </div>
      </RightSidebarPanel>
    </ListPageShell>
  );
}
